/**
 * How a heat-grid cell paints (plots plan Q): one ramp on every surface.
 *
 * Python stamps `heat` 0-1 with polarity folded in; the plot adds the
 * registry's floor (`x-plot.constants.heatFloor`, 14%) so the palest measured
 * cell is still a tint and never reads as blank paper, then mixes the chart's
 * primary over the slide background in CSS (every token carries a fallback,
 * so no surface can paint an unresolved colour).
 */

import {
  chartSegmentHeatColor,
  chartSegmentHeatRgb,
  chartSegmentHeatTextColor,
} from '../../components/chart-segment/heatmap-color'
import { PLOT_REGISTRY } from '../registry'

function registryFloor(): number {
  const constants = (PLOT_REGISTRY['heat-grid'] as { constants?: Record<string, unknown> }).constants
  const floor = constants?.heatFloor
  return typeof floor === 'number' && Number.isFinite(floor) ? floor : 0
}

/** The floor every heat surface paints from (registry constant). */
export const HEAT_FLOOR = registryFloor()

/** Paint intensity for a stamped heat: the floor plus the heat over the rest. */
export function heatIntensity(heat: number): number {
  const clamped = Math.max(0, Math.min(1, heat))
  return HEAT_FLOOR + (1 - HEAT_FLOOR) * clamped
}

/** A cell with no heat (a change column, an empty cell) paints neutral. */
export const HEAT_NEUTRAL_FILL = 'var(--slide-chart-segment-neutral, var(--slide-neutral-light, #edf2f7))'
export const HEAT_NEUTRAL_TEXT = 'var(--slide-text-primary, #111827)'
export const HEAT_EMPTY_TEXT = 'var(--slide-chart-segment-neutral-text, var(--slide-text-muted, #737373))'

export function heatFill(heat: number | null | undefined): string {
  if (typeof heat !== 'number' || !Number.isFinite(heat)) {
    return HEAT_NEUTRAL_FILL
  }
  return chartSegmentHeatColor(heatIntensity(heat))
}

/**
 * `heatFill` resolved to one `rgb()` colour (a chart whose markup is kept
 * outside a browser: the survey gallery). Same ramp, same floor.
 */
export function heatFillRgb(heat: number | null | undefined): string {
  if (typeof heat !== 'number' || !Number.isFinite(heat)) {
    return HEAT_NEUTRAL_FILL
  }
  return chartSegmentHeatRgb(heatIntensity(heat))
}

export function heatText(heat: number | null | undefined): string {
  if (typeof heat !== 'number' || !Number.isFinite(heat)) {
    return HEAT_NEUTRAL_TEXT
  }
  return chartSegmentHeatTextColor(heatIntensity(heat))
}

/** The legend's swatch steps, drawn through the same ramp as the cells. */
export const HEAT_LEGEND_STEPS: ReadonlyArray<number> = [0, 0.25, 0.5, 0.75, 1]
